<template>
  <div>
    <v-tabs
      v-model="activeTab"
      align-tabs="start"
      class="mt-3 mb-2 tabs-on-line nav-button"
      color="#00A3D9"
      center-active
    >
      <v-tab value="nav-returns">
        {{ isMobile && activeTab !== 'nav-returns' ? 'Nav' : 'NAV Returns' }}
      </v-tab>
      <v-tab value="sip-returns">
        {{ isMobile && activeTab !== 'sip-returns' ? 'Sip' : 'SIP Returns' }}
      </v-tab>
      <v-tab value="rolling-returns">
        {{
          isMobile && activeTab !== 'rolling-returns'
            ? 'Rolling'
            : 'Rolling Returns'
        }}
      </v-tab>
    </v-tabs>

    <div class="tab-underline"></div>

    <div
      class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3"
    >
      <div class="d-flex gap-4 flex-wrap">
        <span class="text-muted"></span>
      </div>

      <div
        class="d-flex align-items-center gap-2 period-dropdown"
        v-if="activeTab != 'rolling-returns'"
      >
        <span class="label">Period :</span>
        <select
          v-model="tabPeriods[activeTab]"
          class="form-select border-0 bg-transparent shadow-none w-auto"
        >
          <option
            v-for="period in filteredPeriods"
            :key="period"
            :value="period"
          >
            {{ period }}
          </option>
        </select>
      </div>
      <div
        class="d-flex align-items-center gap-2 period-dropdown"
        v-if="activeTab === 'rolling-returns'"
      >
        <span class="label">Rolling Returns :</span>
        <select
          v-model="selectedRollingReturn"
          class="form-select border-0 bg-transparent shadow-none w-auto"
        >
          <option
            v-for="(label, key) in rollingReturnOptions"
            :key="key"
            :value="key"
          >
            {{ label }}
          </option>
        </select>
      </div>
    </div>
    <apexchart
      v-if="chartReady"
      width="100%"
      height="320"
      type="line"
      :key="chartData.series.map((s) => s.name).join('-')"
      :options="chartOptions"
      :series="chartData.series"
    />
    <p class="text-muted small mt-2" v-if="activeTab === 'rolling-returns'">
      Note: The chart returns for the last 5 years based on the rolling return
      selected. For rolling returns for a different timeframe, please check our
      <a
        :href="`${primeInvestorUrl}/mutual-funds-rolling-returns-calculator/`"
        target="_blank"
        class="underline"
        rel="noopener noreferrer"
      >
        rolling returns calculator
      </a>
    </p>
    <p class="text-muted small mt-2" v-else-if="activeTab === 'nav-returns'">
      Note: Return calculations are based on the NAV movement of the fund for
      the selected period.
    </p>
    <p class="text-muted small mt-2" v-if="activeTab === 'sip-returns'">
      Note: Calculations are based on a SIP amount of ₹10,000 per month at the
      beginning of each month.
    </p>

    <div
      class="table-responsive rolling-returns-table" 
      v-if="activeTab === 'rolling-returns' && rollingReturnStats"
    >
      <table class="custom-table">
        <thead>
          <tr class="rolling-header">
            <th colspan="5">Return Statistics (%)</th>
            <th colspan="5">Return Distribution (% of times)</th>
          </tr>
          <tr>
            <th>Average</th>
            <th>Maximum</th>
            <th>Minimum</th>
            <th>Std. Deviation</th>
            <th v-if="rollingReturnStats.beat_percentage !== null">Beat {{this.indexName}}</th>
            <th>Negative</th>
            <th>0 - 5%</th>
            <th>5 - 10%</th>
            <th>10 - 20%</th>
            <th>More than 20%</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ rollingReturnStats.return_statistics.average }}%</td>
            <td>{{ rollingReturnStats.return_statistics.maximum }}%</td>
            <td>{{ rollingReturnStats.return_statistics.minimum }}%</td>
            <td>{{ rollingReturnStats.return_statistics.std_deviation }}%</td>
            <td v-if="rollingReturnStats.beat_percentage !== null">
              {{ rollingReturnStats.beat_percentage}}%
            </td>
            <td>{{ rollingReturnStats.return_distribution.negative }}%</td>
            <td>{{ rollingReturnStats.return_distribution.zero_to_five }}%</td>
            <td>{{ rollingReturnStats.return_distribution.five_to_ten }}%</td>
            <td>{{ rollingReturnStats.return_distribution.ten_to_twenty }}%</td>
            <td>
              {{ rollingReturnStats.return_distribution.more_than_twenty }}%
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import ApexChart from 'vue3-apexcharts'
import dayjs from 'dayjs'

export default {
  components: {
    apexchart: ApexChart,
  },
  data() {
    return {
      activeTab: 'nav-returns',
      isMobile: window.innerWidth < 500,
      selectedPeriod: '1Y',
      periods: ['1M', '6M', '1Y', '3Y', '5Y', '10Y', 'All'],
      chartData: {
        series: [],
      },
      tabPeriods: {
        'nav-returns': '1Y',
        'sip-returns': '1Y',
        'rolling-returns': '1Y',
      },
      selectedRollingReturn: 'one_year_rolling',
      primeInvestorUrl: process.env.VUE_APP_BASE_URL,
      rollingReturnOptions: {
        one_month_rolling: '1 Month',
        six_month_rolling: '6 Months',
        one_year_rolling: '1 Year',
        three_year_rolling: '3 Years',
        five_year_rolling: '5 Years',
        ten_year_rolling: '10 Years',
      },
      navChartOptions: null,
      sipChartOptions: null,
      rollingChartOptions: null,
      updatedSipData: [],
      updatedNavData: [],
      updatedRollingData: null,
      rollingReturnStats: null,
      chartOptions: {},
      latestNavData: {
        fund: 0,
        nifty: 0,
      },

      chartReady: false,
    }
  },
  computed: {
    navReturns() {
      const data = this.$store.getters.schemeData
      return Array.isArray(data?.naveGraph) ? data.naveGraph : []
    },
    rollingReturnStatisticsValue() {
      const schemeData = this.$store.getters.schemeData
      if (
        schemeData?.rollingReturnStatistics &&
        schemeData.rollingReturnStatistics.length
      ) {
        return schemeData.rollingReturnStatistics || null
      }
      return schemeData?.rollingReturnStatistics || null
    },
    filteredPeriods() {
      if (
        this.activeTab === 'nav-returns' ||
        this.activeTab === 'rolling-returns'
      ) {
        return this.periods.filter((p) => p !== 'All')
      }
      return this.periods
    },
    indexName() {
      const navs = this.navReturns
      const entry = navs.find(
        (item) =>
          item.index_name &&
          item.index_return_pct != null &&
          item.index_return_pct !== ''
      )
      return entry ? entry.index_name : null
    },
    sipReturns() {
      const data = this.$store.getters.schemeData
      return Array.isArray(data?.sipGraph) ? data.sipGraph : []
    },
    rollingReturns() {
      const data = this.$store.getters.schemeData
      return Array.isArray(data?.rollingReturnGraph)
        ? data.rollingReturnGraph
        : []
    },

    schemedetails() {
      const data = this.$store.getters.schemeData
      return data && typeof data === 'object' ? data : {}
    },
    latestNavReturn() {
      return this.navReturns.length
        ? this.navReturns[this.navReturns.length - 1]
        : null
    },
    latestSipReturn() {
      return this.sipReturns.length
        ? this.sipReturns[this.sipReturns.length - 1]
        : null
    },
    latestRollingReturn() {
      return this.rollingReturns.length
        ? this.rollingReturns[this.rollingReturns.length - 1]
        : null
    },
  },
  watch: {
    navReturns: {
      immediate: true,
      handler() {
        this.updateChart()
      },
    },
    selectedRollingReturn(newVal) {
      if (this.activeTab === 'rolling-returns' && newVal) {
        this.fetchRollingReturnsData()
      }
    },
    sipReturns: {
      immediate: true,
      handler() {
        this.updateChart()
      },
    },
    rollingReturns: {
      immediate: true,
      handler() {
        this.updateChart()
      },
    },
    tabPeriods: {
      handler() {
        this.updateChart()
        if (this.activeTab === 'sip-returns') {
          this.fetchSipGraphData()
        } else if (this.activeTab === 'nav-returns') {
          this.fetchNavGraphData()
        } else if (this.activeTab === 'rolling-returns') {
          this.fetchRollingReturnsData()
        }
      },
      deep: true,
    },
    activeTab() {
      this.updateChart()
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  },

  methods: {
    handleResize() {
      this.isMobile = window.innerWidth < 500
    },
    getCutoffDate(range) {
      const now = new Date()
      switch (range) {
        case '1M':
          return new Date(now.setMonth(now.getMonth() - 1))
        case '3M':
          return new Date(now.setMonth(now.getMonth() - 3))
        case '6M':
          return new Date(now.setMonth(now.getMonth() - 6))
        case '1Y':
          return new Date(now.setFullYear(now.getFullYear() - 1))
        case '3Y':
          return new Date(now.setFullYear(now.getFullYear() - 3))
        case '5Y':
          return new Date(now.setFullYear(now.getFullYear() - 5))
        case '10Y':
          return new Date(now.setFullYear(now.getFullYear() - 10))
        default:
          return new Date('2000-01-01')
      }
    },
    async fetchNavGraphData() {
      try {
        const amficodeNav = this.schemedetails.schemedetails?.amfi_code
        const rangeNav = this.tabPeriods[this.activeTab]
        const baseUrl = process.env.VUE_APP_BASE_URL
        const response = await fetch(
          `${baseUrl}/wp-admin/admin-ajax.php?action=get_nav_graph_data_ajax&amficode=${amficodeNav}&range=${rangeNav}`
        )
        const data = await response.json()
        if (data.success && Array.isArray(data.data)) {
          this.updatedNavData = data.data
          this.updateChart(this.updatedNavData)
        }
      } catch (err) {
        console.error('Error fetching SIP Graph Data:', err)
        this.error = 'Failed to load data.'
      } finally {
        this.isLoading = false
      }
    },
    async fetchSipGraphData() {
      try {
        const amficode = this.schemedetails.schemedetails?.amfi_code
        const range = this.tabPeriods[this.activeTab]
        const baseUrl = process.env.VUE_APP_BASE_URL
        const response = await fetch(
          `${baseUrl}/wp-admin/admin-ajax.php?action=get_sip_nav_graph_data_ajax&amficode=${amficode}&range=${range}`
        )
        const data = await response.json()
        if (data.success && Array.isArray(data.data)) {
          this.updatedSipData = data.data
          this.updateChart(this.updatedSipData)
        }
      } catch (err) {
        console.error('Error fetching SIP Graph Data:', err)
        this.error = 'Failed to load data.'
      } finally {
        this.isLoading = false
      }
    },
    async fetchRollingReturnsData() {
      this.isLoading = true
      try {
        const amficode = this.schemedetails.schemedetails?.amfi_code
        const period = this.selectedRollingReturn
        const baseUrl = process.env.VUE_APP_BASE_URL

        const response = await fetch(
          `${baseUrl}/wp-admin/admin-ajax.php?action=get_rolling_returns_data_ajax&amficode=${amficode}&period=${period}&range=5Y`
        )

        const data = await response.json()

        if (data.success && data.data) {
          this.updatedRollingData = data.data
          this.rollingReturnStats = data.data.rollingReturnStatistics || null
          this.updateChart(this.updatedRollingData)
        } else {
          console.error('Unexpected response:', data)
          this.error = 'Failed to load rolling returns data.'
        }
      } catch (err) {
        console.error('Error fetching Rolling Returns Data:', err)
        this.error = 'Failed to load rolling returns data.'
      } finally {
        this.isLoading = false
      }
    },

    updateChart(passedData = null) {
      let rawData = []
      if (this.activeTab === 'rolling-returns') {
        rawData =
          passedData &&
          typeof passedData === 'object' &&
          !Array.isArray(passedData)
            ? passedData.data || []
            : this.updatedRollingData?.data?.length
              ? this.updatedRollingData.data
              : this.rollingReturns

        this.rollingReturnStats =
          passedData &&
          typeof passedData === 'object' &&
          !Array.isArray(passedData)
            ? passedData.rollingReturnStatistics || null
            : this.updatedRollingData?.rollingReturnStatistics ||
              this.rollingReturnStatisticsValue
        this.chartOptions = this.getRollingChartOptions()
      } else if (this.activeTab === 'sip-returns') {
        rawData = passedData?.length
          ? passedData
          : this.updatedSipData.length
            ? this.updatedSipData
            : this.sipReturns
        this.chartOptions = this.getSipChartOptions()
      } else if (this.activeTab === 'nav-returns') {
        rawData = passedData?.length
          ? passedData
          : this.updatedNavData.length
            ? this.updatedNavData
            : this.navReturns
        this.chartOptions = this.getNavChartOptions()
      }

      let filteredData = rawData.map((item) => JSON.parse(JSON.stringify(item)))

      filteredData.sort((a, b) => {
        const dateA = new Date(a.date)
        const dateB = new Date(b.date)
        return dateA - dateB
      })

      let series = []
      if (this.activeTab === 'rolling-returns') {
        series = this.prepareRollingReturnsChart(filteredData)
      } else if (this.activeTab === 'sip-returns') {
        series = this.prepareSipReturnsChart(filteredData)
      } else if (this.activeTab === 'nav-returns') {
        series = this.prepareNavReturnsChart(filteredData)
      }

      this.chartData = { series }
      this.$nextTick(() => {
        this.chartReady = false
        this.$nextTick(() => {
          this.chartReady = true
        })
      })

      this.chartReady = true
    },

    prepareNavReturnsChart(filteredData) {
      if (!Array.isArray(filteredData) || filteredData.length === 0) {
        console.warn('No NAV return data available.')
        return []
      }

      const fundData = []
      const niftyData = []

      let totalFundReturn = 0
      let totalNiftyReturn = 0

      let minFundReturn = Infinity
      let maxFundReturn = -Infinity
      let minNiftyReturn = Infinity
      let maxNiftyReturn = -Infinity

      filteredData.forEach((item) => {
        const date = item.date

        if (item.return_pct != null) {
          const fundReturn = parseFloat(item.return_pct)
          if (!isNaN(fundReturn)) {
            fundData.push([date, fundReturn])
            totalFundReturn += fundReturn
            minFundReturn = Math.min(minFundReturn, fundReturn)
            maxFundReturn = Math.max(maxFundReturn, fundReturn)
          }
        }

        if (item.index_return_pct != null && item.index_return_pct != 0) {
          const niftyReturn = parseFloat(item.index_return_pct)
          if (!isNaN(niftyReturn)) {
            niftyData.push([date, niftyReturn])
            totalNiftyReturn += niftyReturn
            minNiftyReturn = Math.min(minNiftyReturn, niftyReturn)
            maxNiftyReturn = Math.max(maxNiftyReturn, niftyReturn)
          }
        }
      })

      const hasFund = fundData.length > 0
      const hasNifty = niftyData.length > 0

      const avgFundReturn = hasFund ? totalFundReturn / fundData.length : null
      const avgNiftyReturn = hasNifty
        ? totalNiftyReturn / niftyData.length
        : null

      const latestData = filteredData[filteredData.length - 1] || {}
      this.latestNavData.fund = latestData.return_pct ?? ''
      this.latestNavData.nifty = latestData.index_return_pct ?? ''

      const series = []
      if (hasFund) {
        series.push({
          name: 'Fund Return',
          data: fundData,
        })
      }
      if (hasNifty) {
        series.push({
          name: `${this.indexName}`,
          data: niftyData,
        })
      }

      const yMin =
        hasFund && hasNifty
          ? Math.min(minFundReturn, minNiftyReturn)
          : hasFund
            ? minFundReturn
            : hasNifty
              ? minNiftyReturn
              : 0

      const yMax =
        hasFund && hasNifty
          ? Math.max(maxFundReturn, maxNiftyReturn)
          : hasFund
            ? maxFundReturn
            : hasNifty
              ? maxNiftyReturn
              : 10

      this.chartOptions = {
        ...this.getNavChartOptions(),
        yaxis: {
          min: yMin - 2,
          max: yMax + 2,
          labels: {
            formatter(value) {
              if (avgFundReturn !== null && value === avgFundReturn) {
                return avgFundReturn.toFixed(2) + '%'
              }
              if (avgNiftyReturn !== null && value === avgNiftyReturn) {
                return avgNiftyReturn.toFixed(2) + '%'
              }
              return typeof value === 'number' ? value.toFixed(2) + '%' : ''
            },
          },
        },
        tooltip: {
          shared: true,
          x: {
            format: 'yyyy-MM-dd',
          },
          custom: ({ dataPointIndex }) => {
            const data = filteredData[dataPointIndex]
            if (!data) return ''

            const date = data.date
            const fundNav =
              data.nav != null && data.nav !== 0
                ? `₹${data.nav.toFixed(2)}`
                : ''

            const indexNav =
              data.index_nav != null && data.index_nav !== 0
                ? `${data.index_nav.toFixed(2)}`
                : ''

            const fundReturn =
              data.return_pct != null && data.return_pct !== 0
                ? `${data.return_pct.toFixed(2)}%`
                : ''

            const niftyReturn =
              data.index_return_pct != null && data.index_return_pct !== 0
                ? `${data.index_return_pct.toFixed(2)}%`
                : ''

            let content = `<div style="padding: 8px; font-size: 13px;"><strong>${date}</strong><br/>`

            if (fundReturn) content += `<b>Fund Return:</b> ${fundReturn}<br/>`
            if (fundNav) content += `<b>Fund NAV:</b> ${fundNav}<br/>`
            if (niftyReturn)
              content += `<b>Index Return:</b> ${niftyReturn}<br/>`
            if (indexNav) content += `<b>Index NAV:</b> ${indexNav}<br/>`

            content += `</div>`
            return content
          },
        },

        legend: {
          show: hasFund || hasNifty,
          showForSingleSeries: true,
          showForNullSeries: false,
          position: 'top',
          labels: {
            colors: '#333',
          },
        },
      }

      return series
    },
    getNavChartOptions() {
      const base = this.getCommonChartOptions()
      base.yaxis = {
        show: true,
        labels: {
          formatter: (value) => (value * 100).toFixed(2) + '%',
        },
      }
      base.tooltip = {
        ...base.tooltip,
        x: {
          format: 'yyyy-MM-dd',
        },
      }
      return base
    },
    prepareRollingReturnsChart(filteredData) {
      const oneYearReturns = filteredData.map((item) =>
        item?.one_year_return != null ? parseFloat(item.one_year_return) : null
      )
      const niftyOneYearReturns = filteredData.map((item) =>
        item?.nifty_one_year_return != null
          ? parseFloat(item.nifty_one_year_return)
          : null
      )
      const chartDates = filteredData.map((item) =>
        dayjs(item.date || item.asofdate).valueOf()
      )

      const hasFundData = oneYearReturns.some((val) => val !== null)
      const hasNiftyData = niftyOneYearReturns.some((val) => val !== null)

      this.latestNavData = {
        fund: hasFundData ? this.calculateAverage(oneYearReturns) : null,
        nifty: hasNiftyData ? this.calculateAverage(niftyOneYearReturns) : null,
      }

      const series = []

      if (hasFundData) {
        const fundData = chartDates.map((date, i) => [
          date,
          oneYearReturns[i] ?? null,
        ])
        series.push({
          name: 'Fund',
          data: fundData,
        })
      }

      if (hasNiftyData) {
        const niftyData = chartDates.map((date, i) => [
          date,
          niftyOneYearReturns[i] ?? null,
        ])
        series.push({
          name: `${this.indexName}`,
          data: niftyData,
        })
      }

      return series
    },
    getRollingChartOptions() {
      const base = this.getCommonChartOptions?.() || {}

      base.legend = {
        show: true,
        showForSingleSeries: true,
        showForNullSeries: true,
        position: 'top',
        labels: {
          colors: '#333',
        },
      }

      base.yaxis = {
        show: true,
        labels: {
          formatter: (value) => `${value.toFixed(2)}%`,
        },
      }

      base.tooltip = {
        shared: true,
        x: {
          format: 'yyyy-MM-dd',
        },
        y: {
          formatter: (value) => `${value?.toFixed(2)}%`,
        },
      }

      return base
    },

    calculateAverage(values) {
      const validValues = values.filter((val) => val !== null)
      if (validValues.length > 0) {
        return (
          validValues.reduce((a, b) => a + b, 0) / validValues.length
        ).toFixed(2)
      }
      return 0
    },
    getCommonChartOptions() {
      return {
        chart: { type: 'line', zoom: { enabled: false } },
        xaxis: {
          type: 'datetime',
          labels: {
            rotate: -45,
          },
          tickAmount: 6,
        },
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'solid' },
        colors: ['#0377a6', '#1fd19f'],
        grid: { show: false },
        tooltip: {
          x: {
            format: 'yyyy-MM-dd',
          },
          y: {
            formatter: (value) => {
              if (value >= 10000000) return value / 10000000 + ' Cr'
              else if (value >= 100000) return value / 100000 + ' L'
              else if (value >= 1000) return value / 1000 + 'k'
              return value
            },
          },
        },
      }
    },

    getSipChartOptions() {
      return {
        chart: {
          id: 'sip-returns-chart',
          toolbar: { show: true },
          zoom: { enabled: true },
        },
        xaxis: {
          type: 'datetime',
          labels: {
            xaxis: {
              type: 'datetime',
              labels: {
                formatter: (value) => {
                  const date = new Date(value)
                  const yyyy = date.getFullYear()
                  const mm = String(date.getMonth() + 1).padStart(2, '0')
                  const dd = String(date.getDate()).padStart(2, '0')
                  return `${yyyy}-${mm}-${dd}`
                },
              },
            },
          },
        },
        yaxis: {
          show: true,
          min: 0,

          labels: {
            formatter: (value) => {
              if (value >= 1e7) return `${Math.round(value / 1e7)} Cr`
              if (value >= 1e5) return `${Math.round(value / 1e5)} L`
              if (value >= 1e3) return `${Math.round(value / 1e3)} K`
              return `${value.toLocaleString('en-IN', {
                maximumFractionDigits: 2,
              })}`
            },
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (value) =>
              new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
                maximumFractionDigits: 2,
              }).format(value),
          },
        },
        legend: {
          position: 'top',
          horizontalAlign: 'center',
        },
        stroke: {
          curve: 'smooth',
        },
        grid: {
          show: true,
        },
        dataLabels: {
          enabled: false,
        },
      }
    },

    prepareSipReturnsChart(filteredData) {
      if (
        !filteredData ||
        !Array.isArray(filteredData) ||
        filteredData.length === 0
      ) {
        console.warn('No SIP Data available.')
        return []
      }

      const investedData = []
      const valueData = []
      let minValue = Infinity
      let maxValue = -Infinity

      filteredData.forEach((item) => {
        const date = dayjs(item.date || item.asofdate).valueOf()
        const invested = parseFloat(parseFloat(item.invested || 0).toFixed(2))
        const value = parseFloat(parseFloat(item.value || 0).toFixed(2))

        if (isNaN(invested) || isNaN(value)) return

        investedData.push([date, invested])
        valueData.push([date, value])

        minValue = Math.min(minValue, invested, value)
        maxValue = Math.max(maxValue, invested, value)
      })

      if (minValue === Infinity || maxValue === -Infinity) {
        return []
      }
      this.chartOptions.yaxis.min = Math.floor(minValue * 0.9)
      this.chartOptions.yaxis.max = Math.ceil(maxValue * 1.1)

      this.latestNavData = {
        fund: (filteredData.at(-1)?.value || 0 / 1e5).toFixed(2),
        nifty: (filteredData.at(-1)?.invested || 0 / 1e5).toFixed(2),
      }

      return [
        {
          name: 'Invested',
          data: investedData,
        },
        {
          name: 'Current Value',
          data: valueData,
        },
      ]
    },
  },
}
</script>

<style scoped>
::v-deep(.vue-apexcharts .apexcharts-canvas .apexcharts-toolbar) {
  display: none !important;
}
.rolling-returns-table {
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.25);
    border-radius: 10px;
}
.text-info {
  color: #3ab5f5;
}
.text-success {
  color: #5cb85c;
}
.label {
  font-weight: 500;
}
.v-slide-group__prev,
.v-slide-group__next {
  display: none !important;
}

::v-deep(.nav-button .v-slide-group__container .v-slide-group__content button) {
  background: transparent !important;
}

::v-deep(
  .nav-button .v-slide-group__container .v-slide-group__content button:hover
) {
  color: black !important;
}

.tabs-on-line {
  margin-bottom: 0;
  z-index: 2;
  position: relative;
}

.tab-underline {
  height: 1px;
  background-color: #ccc;
  border: 0.25px solid #918d99;
  border-radius: 5px;
  position: relative;
  top: -10px;
  z-index: 1;
}
.custom-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #ccc;
  margin: unset !important;
}

.custom-table th,
.custom-table td {
  border: 1px solid #ccc;
  padding: 12px;
  text-align: center;
  font-size: 14px;
}

.custom-table thead th {
  font-weight: bold;
}

.custom-table th[colspan] {
  font-size: 15px;
  border-right: 1px solid #fff;
}

.rolling-header {
  background-color: #003c50;
  color: white;
}
</style>
